import { useCallback, useRef, useState } from "react";
import { ModalShell } from "@/components/ModalShell";

/**
 * Asking before doing something you cannot undo.
 *
 * HQ-NATIVE-DIALOG-001. HQ asks 141 questions through window.confirm,
 * window.alert and window.prompt. Those are not merely unstyled: the browser
 * dialog blocks the whole tab, it cannot say which record is about to be
 * deleted in any way the page controls, its buttons read "OK" and "Cancel"
 * whatever the question was, and some browsers let a user tick "prevent this
 * page from creating more dialogs" — after which every later confirm silently
 * returns false and every alert silently vanishes. A delete that quietly
 * becomes a no-op, with no message, is the worst outcome a confirmation can
 * have.
 *
 * This asks in the page, on the same accessible dialog every other HQ dialog
 * uses, and it names the thing:
 *
 *     const { ask, dialog } = useConfirm();
 *     …
 *     if (!(await ask({ title: "Delete this objective?",
 *                       body: `"${o.title}" and its ${n} tasks.`,
 *                       confirmLabel: "Delete objective", danger: true }))) return;
 *     …
 *     return <>{dialog}…</>;
 *
 * `ask` resolves false if the dialog is dismissed, which is what Cancel meant
 * before — so a converted call site keeps its shape.
 */
export interface ConfirmOptions {
  title: string;
  /** What exactly is about to happen, named. */
  body?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Red button, for something destructive. */
  danger?: boolean;
}

export function useConfirm() {
  const [open, setOpen] = useState<ConfirmOptions | null>(null);
  const resolver = useRef<((ok: boolean) => void) | null>(null);

  const settle = useCallback((ok: boolean) => {
    setOpen(null);
    resolver.current?.(ok);
    resolver.current = null;
  }, []);

  const ask = useCallback((opts: ConfirmOptions | string) => {
    const options = typeof opts === "string" ? { title: opts } : opts;
    // A second question while one is open would strand the first promise
    // forever. Answer it as cancelled rather than leaving the caller waiting.
    resolver.current?.(false);
    setOpen(options);
    return new Promise<boolean>(resolve => { resolver.current = resolve; });
  }, []);

  const dialog = open ? (
    <ModalShell title={open.title} onClose={() => settle(false)} closeLabel="Cancel">
      {open.body != null && <div className="mb-4 text-sm text-gray2">{open.body}</div>}
      <div className="flex justify-end gap-2">
        <button type="button" className="btn btn-ghost" onClick={() => settle(false)}>
          {open.cancelLabel ?? "Cancel"}
        </button>
        <button
          type="button"
          autoFocus
          className={open.danger ? "btn bg-bad text-white hover:bg-bad/90" : "btn"}
          onClick={() => settle(true)}
        >
          {open.confirmLabel ?? "Confirm"}
        </button>
      </div>
    </ModalShell>
  ) : null;

  return { ask, dialog };
}

/**
 * Telling someone something, without stopping the browser to do it.
 *
 * The alert() half of the same finding. Resolves when it is dismissed, so a
 * call site that awaited the interruption still reads the same way.
 */
export function useNotice() {
  const [open, setOpen] = useState<{ title: string; body?: React.ReactNode } | null>(null);
  const resolver = useRef<(() => void) | null>(null);

  const close = useCallback(() => {
    setOpen(null);
    resolver.current?.();
    resolver.current = null;
  }, []);

  const say = useCallback((opts: { title: string; body?: React.ReactNode } | string) => {
    resolver.current?.();
    setOpen(typeof opts === "string" ? { title: opts } : opts);
    return new Promise<void>(resolve => { resolver.current = resolve; });
  }, []);

  const notice = open ? (
    <ModalShell title={open.title} onClose={close}>
      {open.body != null && <div className="mb-4 text-sm text-gray2">{open.body}</div>}
      <div className="flex justify-end">
        <button type="button" autoFocus className="btn" onClick={close}>OK</button>
      </div>
    </ModalShell>
  ) : null;

  return { say, notice };
}
